import { I18n } from '@calendly/i18n';
import { BrandLogo } from '@calendly/ui/components/brand-logo';
import { Button } from '@calendly/ui/components/button';
import { ArrowExpandIcon } from '@calendly/ui/components/icons';
import { InlineBanner } from '@calendly/ui/components/inline-banner';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import hubspotIconSrc from 'components/integrations/elements/provider_logo/logos/hubspot.svg';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import { useIntegrationsContext } from './integrations_provider';
import { RecordingRulesSwitch } from './recording_rules_switch';
import { INTEGRATION_WITHOUT_TOGGLE } from '../../constants';
const integrationCardCss = "iedjoru";
const integrationCardMobileCss = "i1h5szkx";
const IntegrationCardContainer = ({
  isPhone,
  ...props
}) => <div {...props} className={classNames(integrationCardCss, {
  [integrationCardMobileCss]: isPhone
}, props.className)} />;
const logoWrapperCss = "l1mgehnm";
const LogoWrapper = props => <div {...props} className={classNames(logoWrapperCss, props.className)} />;
const logoCss = "lj3gvad";
const integrationNameCss = "i9xnoom";
const IntegrationName = props => <div {...props} className={classNames(integrationNameCss, props.className)} />;
const switchContainerCss = "sgq7gkh";
const SwitchContainer = props => <div {...props} className={classNames(switchContainerCss, props.className)} />;
const pushRecapsTextCss = "p1jq6s89";
const pushRecapsTextMobileCss = "p27qff8";
const headerRowCss = "h1c0vxeq";
const HeaderRow = props => <div {...props} className={classNames(headerRowCss, props.className)} />;
const controlsRowCss = "c1crqj8b";
const ControlsRow = props => <div {...props} className={classNames(controlsRowCss, props.className)} />;
const IntegrationSettingsButton = ({
  href,
  children
}) => <Button as="a" href={href} target="_blank" rel="noopener noreferrer" decoration="ghost">
    {children}
    <ArrowExpandIcon size="standard" />
  </Button>;
const getLogo = name => {
  if (name === 'hubspot') {
    return <img src={hubspotIconSrc} alt="Hubspot" className={logoCss} />;
  }

  // Use BrandLogo for other integrations
  let brandName = name;
  if (name === 'salesforce') {
    brandName = 'salesforce';
  } else if (name === 'zapier') {
    brandName = 'zapier';
  }
  return <BrandLogo name={brandName} className={logoCss} />;
};
const capitalizeFirst = str => str.charAt(0).toUpperCase() + str.slice(1);
const SalesforceUpgradeBannerCss = "s176o19l";
const SalesforceUpgradeBanner = () => <InlineBanner className={SalesforceUpgradeBannerCss} variant="danger" title={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.salesforce_upgrade_banner.title')} message={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.salesforce_upgrade_banner.message')} isDismissible={false} primaryButton={{
  decoration: 'secondary-outline',
  label: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.salesforce_upgrade_banner.button_label'),
  onClick: () => {
    window.location.href = '/integrations/salesforce';
  }
}} />;
export const IntegrationsCard = ({
  integration,
  onToggle,
  setIsDirty
}) => {
  const isOwnerOrAdmin = useSelector(isAdminOrOwnerSelector);
  const {
    name,
    enabled,
    connected
  } = integration;
  const {
    isSalesforcePackageOutdated
  } = useIntegrationsContext();
  const displayName = capitalizeFirst(name);
  const settingsLink = `/integrations/${name}`;
  const isPhone = useDeviceType('isPhone');
  const showToggle = connected && !INTEGRATION_WITHOUT_TOGGLE.includes(name);
  const showSalesforceUpgradeBanner = name === 'salesforce' && isSalesforcePackageOutdated;
  if (isPhone) {
    return <IntegrationCardContainer isPhone={isPhone}>
        <HeaderRow>
          <LogoWrapper>
            {getLogo(name)}
          </LogoWrapper>
          <IntegrationName>
            <BodyText size="medium" weight="bold">
              {displayName}
            </BodyText>
          </IntegrationName>
        </HeaderRow>
        {showToggle && <ControlsRow>
            {isOwnerOrAdmin && <>
                <SwitchContainer>
                  <RecordingRulesSwitch value={enabled} onChange={onToggle} setIsDirty={setIsDirty} />
                </SwitchContainer>
                <BodyText size="medium" className={classNames(pushRecapsTextCss, pushRecapsTextMobileCss)}>
                  {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.push_recaps')}
                </BodyText>
              </>}
            <IntegrationSettingsButton href={settingsLink}>
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.settings')}
            </IntegrationSettingsButton>
          </ControlsRow>}
        {!connected && <ControlsRow>
            <IntegrationSettingsButton href={settingsLink}>
              {`${I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.connect_integration')} ${displayName}`}
            </IntegrationSettingsButton>
          </ControlsRow>}
        {connected && !showToggle && <ControlsRow>
            <IntegrationSettingsButton href={settingsLink}>
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.settings')}
            </IntegrationSettingsButton>
          </ControlsRow>}
        {showSalesforceUpgradeBanner && <SalesforceUpgradeBanner />}
      </IntegrationCardContainer>;
  }
  return <IntegrationCardContainer>
      <LogoWrapper>
        {getLogo(name)}
      </LogoWrapper>
      <IntegrationName>
        <BodyText size="medium" weight="bold">
          {displayName}
        </BodyText>
      </IntegrationName>
      {showToggle && isOwnerOrAdmin && <>
          <SwitchContainer>
            <RecordingRulesSwitch value={enabled} onChange={onToggle} setIsDirty={setIsDirty} />
          </SwitchContainer>
          <BodyText size="medium" className={pushRecapsTextCss}>
            {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.push_recaps')}
          </BodyText>
        </>}
      <IntegrationSettingsButton href={settingsLink}>
        {connected ? I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.settings') : `${I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.connect_integration')} ${displayName}`}
      </IntegrationSettingsButton>
      {showSalesforceUpgradeBanner && <SalesforceUpgradeBanner />}
    </IntegrationCardContainer>;
};

require("./integrations_card.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./integrations_card.js");